import React, { useRef, useState } from "react";
import "./add_item_available_packing.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType, TableProps } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps, TableRowSelection } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from "swr";
import axios from "axios";

interface AddItemPackingProps {
    salesOrderNo?: string;
    onSelectPickingListItems: (pickingListNos: string[]) => void;
    onClose: () => void;
    importedPickingListNos: Set<string>;
}


const AddItemPacking: React.FC<AddItemPackingProps> = ({ salesOrderNo, onSelectPickingListItems, onClose, importedPickingListNos }) => {
    const [isprogress, setIsprogress] = useState(false);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    interface DataType {
        key: string;
        no_picking: string;
        date_picking: string;
        sales_order: string;
    }

    type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

    type DataIndex = keyof DataType;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data, error, isLoading } = useSWR<DataType[]>(
        salesOrderNo ? `${backendUrl}/api/picking-lists-by-sales-order?soNo=${salesOrderNo}` : null,
        fetcher
    );

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedPickingLists, setSelectedPickingLists] = useState<DataType[]>([]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex).replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Picking List No',
            dataIndex: 'no_picking',
            key: 'no_picking',
            width: '30%',
            ...getColumnSearchProps('no_picking'),
            sorter: (a, b) => a.no_picking.localeCompare(b.no_picking),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Date',
            dataIndex: 'date_picking',
            key: 'date_picking',
            width: '20%',
            ...getColumnSearchProps('date_picking'),
            sorter: (a, b) => new Date(a.date_picking).getTime() - new Date(b.date_picking).getTime(),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Sales Order',
            dataIndex: 'sales_order',
            key: 'sales_order',
            width: '30%',
            ...getColumnSearchProps('sales_order'),
            sorter: (a, b) => a.sales_order.localeCompare(b.sales_order),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const rowSelection: TableRowSelection<DataType> = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: DataType[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
            setSelectedPickingLists(newSelectedRows);
        },
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
            {
                key: 'odd',
                text: 'Select Odd Row',
                onSelect: (changeableRowKeys) => {
                    let newSelectedRowKeys: React.Key[] = [];
                    newSelectedRowKeys = changeableRowKeys.filter((_, index) => {
                        if (index % 2 !== 0) {
                            return false;
                        }
                        return true;
                    });
                    setSelectedRowKeys(newSelectedRowKeys);
                },
            },
            {
                key: 'even',
                text: 'Select Even Row',
                onSelect: (changeableRowKeys) => {
                    let newSelectedRowKeys: React.Key[] = [];
                    newSelectedRowKeys = changeableRowKeys.filter((_, index) => {
                        if (index % 2 !== 0) {
                            return true;
                        }
                        return false;
                    });
                    setSelectedRowKeys(newSelectedRowKeys);
                },
            },
        ],
        getCheckboxProps: (record: DataType) => ({
            disabled: importedPickingListNos.has(record.no_picking),
            name: record.no_picking,
        }),
    };

    const formattedData: DataType[] = data ? data.map((item: any) => ({
        key: item.id.toString(),
        no_picking: item.no_picking,
        date_picking: item.date_picking,
        sales_order: item.sales_order,
    })) : [];

    return (

        <div id="i2bt" style={transformStyle}>
            <div className="gjs-grid-row" id="i76b">
                <div className="gjs-grid-column" id="im7n">
                    <Table<DataType>
                        columns={columns}
                        dataSource={formattedData}
                        pagination={false}
                        loading={isLoading || isprogress}
                        rowSelection={rowSelection}
                        onRow={(record) => {
                            const isImported = importedPickingListNos.has(record.no_picking);
                            return {
                                className: isImported ? 'imported-picking-list-row' : '',
                            };
                        }}
                    />
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Button
                            type="primary"
                            onClick={async () => {
                                setIsprogress(true);
                                try {
                                    const pickingListNosToImport = selectedPickingLists.map(item => item.no_picking);
                                    await onSelectPickingListItems(pickingListNosToImport);
                                    onClose();
                                } catch (err) {
                                    console.error(err);
                                } finally {
                                    setIsprogress(false);
                                }
                            }}
                            disabled={selectedRowKeys.length === 0 || isprogress}
                            loading={isprogress}
                        >
                            Import
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddItemPacking;